/**
 * Componente de Etiqueta de Producto para Impresión
 * Genera una etiqueta con información del paciente y código de barras visual
 */
import { QRCodeCanvas } from 'qrcode.react'

export default function ProductLabel({ request, patient, onClose }) {
  // Generar número de serie con formato (año actual / número con 8 dígitos)
  const currentYear = new Date().getFullYear().toString().slice(-2);
  const serialNumber = `${currentYear}/${(request.consecutive || request.id).toString().padStart(8, '0')}`;
  const barcodeValue = (request.consecutive || request.id).toString().padStart(8, '0');

  // Fecha de entrega
  const deliveryDate = request.delivered_at || request.deliveredAt || request.delivered_date || request.deliveredDate || new Date();
  const formattedDate = new Date(deliveryDate).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Modal con vista previa */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 no-print">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Vista Previa de Etiqueta</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          <div className="flex justify-center mb-4">
            <div className="bg-gray-100 p-4 rounded-lg">
              <div id="label-to-print" className="label-content bg-white">
                {/* Contenido de la etiqueta */}
                <LabelContent 
                  request={request}
                  patient={patient}
                  serialNumber={serialNumber}
                  formattedDate={formattedDate}
                  barcodeValue={barcodeValue}
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition"
            >
              Cancelar
            </button>
            <button
              onClick={handlePrint}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-semibold flex items-center gap-2"
            >
              🖨️ Imprimir Etiqueta
            </button>
          </div>
        </div>
      </div>

      {/* Contenido para impresión (oculto en pantalla) */}
      <div className="print-only">
        <LabelContent 
          request={request}
          patient={patient}
          serialNumber={serialNumber}
          formattedDate={formattedDate}
          barcodeValue={barcodeValue}
        />
      </div>

      {/* Estilos de impresión */}
      <style>{`
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100mm !important;
            height: 50mm !important;
            overflow: hidden !important;
          }
          body * {
            visibility: hidden !important;
          }
          .print-only, .print-only * {
            visibility: visible !important;
          }
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100mm !important;
            height: 50mm !important;
            margin: 0 !important;
            padding: 0 !important;
            page-break-after: avoid !important;
            page-break-before: avoid !important;
            page-break-inside: avoid !important;
            background: white !important;
            overflow: hidden !important;
          }
          @page {
            size: 100mm 50mm landscape;
            margin: 0;
          }
        }
        .no-print {
          display: block;
        }
        .print-only {
          display: none;
        }
      `}</style>
    </>
  );
}

/**
 * Contenido de la etiqueta (reutilizable para vista previa e impresión)
 */
function LabelContent({ request, patient, serialNumber, formattedDate, barcodeValue }) {
  return (
    <div className="label-content" style={{
      width: '100mm',
      height: '50mm',
      padding: '3mm 4mm',
      border: '1px solid #000',
      fontFamily: 'Arial, sans-serif',
      fontSize: '10pt',
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      justifyContent: 'space-between'
    }}>
      {/* Fila superior: Información + Logo + QR */}
      <div style={{
        display: 'flex',
        gap: '6mm',
        alignItems: 'flex-start',
        minHeight: '30mm'
      }}>
        {/* Columna izquierda: información */}
        <div style={{
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.8mm'
        }}>
          <p style={{ margin: 0, fontSize: '9pt' }}><strong>Fecha:</strong> {formattedDate}</p>
          <p style={{ margin: 0, fontSize: '9pt' }}><strong>Nº de Solicitud:</strong> {serialNumber}</p>
          <p style={{ margin: 0, fontSize: '9pt' }}><strong>Paciente:</strong> {patient?.name || request.patient_name || 'N/A'}</p>
          <p style={{ margin: 0, fontSize: '9pt' }}><strong>Prescriptor:</strong> {request.doctor_name || request.doctorName || 'N/A'}</p>
          <p style={{ margin: 0, fontSize: '9pt' }}><strong>Clínica:</strong> {request.clinic || request.clinic_name || request.clinicName || patient?.clinic || 'N/A'}</p>
        </div>

        {/* Columna derecha: Logo + QR */}
        <div style={{
          flex: '0 0 32mm',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '3mm'
        }}>
          {/* Logo */}
          <div style={{
            width: '32mm',
            height: 'auto'
          }}>
            <img 
              src="/iconos/logo negro.jpeg" 
              alt="Lucván" 
              style={{ width: '100%', height: 'auto', maxHeight: '11mm' }} 
            />
          </div>

          {/* QR */}
          <div style={{ 
            width: '20mm',
            height: '20mm',
            border: '1px solid #ddd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <QRCodeCanvas 
              value="https://lucvanlatam.com" 
              size={72}
              level="L"
              includeMargin={false}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        </div>
      </div>

      {/* Dirección centrada abajo */}
      <div style={{
        textAlign: 'center',
        fontSize: '7.5pt',
        lineHeight: '1.2'
      }}>
        <p style={{ margin: 0, fontWeight: 'bold' }}>LATAM:</p>
        <p style={{ margin: 0 }}>200m al oeste del Hospital San Rafael de Alajuela, Costa Rica.</p>
      </div>
    </div>
  );
}
